/* @jsx h */
import { defineComponent, ref, h } from "vue";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { DateRangePicker } from "@lemmonhq/uikit";

import { BIReport } from "@lemmonhq/types";
import { api } from "@app/data/api";

export type ReportDetailsProps = {
  report: BIReport;
  isOpen: boolean;
  onClose: Function;
};
export const ReportDetails = defineComponent<ReportDetailsProps>({
  name: "ReportDetails",
  props: ["report", "isOpen", "onClose"],
  setup(props) {
    const { locale } = useLocalization();
    const isLoading = ref(false);
    const downloadUrl = ref<string | null>(null);
    const reportRunId = ref<{ id: string; fileName: string } | null>(null);
    const pollingInterval = ref<number | null>(null);
    const dateRange = ref({
      startDate: new Date().toISOString().split("T")[0],
      endDate: new Date().toISOString().split("T")[0],
      type: "today" as const,
    });
    const reportType = ref<"summary" | "detailed">(
      props.report.reportTypes[0]! as any,
    );

    const handleDateRangeChange = (range: any) => {
      dateRange.value = range;
    };

    const stopPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
      }
    };

    const startReportGeneration = async () => {
      if (!props.report) return;

      isLoading.value = true;
      downloadUrl.value = null;
      stopPolling();

      try {
        const run = await api.createReportRun({
          type: props.report.id,
          reportType: reportType.value,
          startDate: dateRange.value.startDate,
          endDate: dateRange.value.endDate,
          locale: locale.value,
        });
        reportRunId.value = run;

        pollingInterval.value = setInterval(async () => {
          const status = await api.getReportRun(reportRunId.value!.id);
          if (status.status === "complete") {
            isLoading.value = false;
            const qs = new URLSearchParams({
              filename: run.fileName,
            });
            downloadUrl.value = `/api/v2/bi/${
              run.id
            }/download?${qs.toString()}`;
            stopPolling();
          } else if (status.status === "errored") {
            isLoading.value = false;
            stopPolling();
            // TODO: Show error message
          }
        }, 6000) as unknown as number;
      } catch (error) {
        console.error("Failed to generate report:", error);
        isLoading.value = false;
      }
    };

    return () => (
      <RightSheet
        isOpen={props.isOpen}
        onClose={() => {
          stopPolling();
          props.onClose();
        }}
        title={props.report?.name}
      >
        <div class="flex min-h-full flex-col bg-base-200/30">
          <div class="border-b border-neutral/15 bg-base-100 px-5 py-5 sm:px-6">
            <div class="flex items-start gap-4">
              <div class="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <span class="material-symbols-outlined text-2xl">
                  description
                </span>
              </div>
              <div>
                <p class="text-sm font-medium text-base-content">
                  Prepare your export
                </p>
                <p class="mt-1 text-xs leading-relaxed text-base-content/55">
                  Choose what to include. We will prepare a secure file for you
                  to download.
                </p>
              </div>
            </div>
          </div>

          <div class="flex-1 space-y-5 p-4 sm:p-6">
            {downloadUrl.value ? null : (
              <>
                {!props.report.noDateRange && (
                  <section class="rounded-2xl border border-neutral/15 bg-base-100 p-4 shadow-sm">
                    <div class="mb-4 flex items-center gap-3">
                      <div class="flex size-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <span class="material-symbols-outlined text-lg">
                          date_range
                        </span>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-base-content">
                          Date range
                        </label>
                        <p class="text-xs text-base-content/45">
                          Select the period covered by this report
                        </p>
                      </div>
                    </div>
                    <DateRangePicker
                      value={dateRange.value}
                      onChange={handleDateRangeChange}
                    />
                  </section>
                )}

                {props.report.reportTypes.length > 1 && (
                  <section class="rounded-2xl border border-neutral/15 bg-base-100 p-4 shadow-sm">
                    <div class="mb-4 flex items-center gap-3">
                      <div class="flex size-9 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                        <span class="material-symbols-outlined text-lg">
                          tune
                        </span>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-base-content">
                          Report type
                        </label>
                        <p class="text-xs text-base-content/45">
                          Pick the level of detail you need
                        </p>
                      </div>
                    </div>
                    <div class="grid gap-2 sm:grid-cols-2">
                      {props.report.reportTypes.map((type) => (
                        <label
                          key={type}
                          class={[
                            "flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors",
                            reportType.value === type
                              ? "border-primary bg-primary/10"
                              : "border-neutral/15 hover:bg-base-200/60",
                          ].join(" ")}
                        >
                          <input
                            type="radio"
                            name="reportType"
                            class="radio radio-primary radio-sm"
                            value={type}
                            checked={reportType.value === type}
                            onChange={() =>
                              (reportType.value = type as
                                | "summary"
                                | "detailed")
                            }
                          />
                          <span class="flex-1">
                            <span class="block text-sm font-medium capitalize text-base-content">
                              {type}
                            </span>
                            <span class="block text-[11px] text-base-content/45">
                              {type === "summary"
                                ? "Key totals and highlights"
                                : "Every available row"}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </section>
                )}
              </>
            )}

            {downloadUrl.value ? (
              <div class="flex flex-col items-center rounded-3xl border border-success/20 bg-base-100 px-5 py-10 text-center shadow-sm">
                <div class="mb-5 flex size-16 items-center justify-center rounded-full bg-success/10 text-success">
                  <span class="material-symbols-outlined text-4xl">
                    task_alt
                  </span>
                </div>
                <h3 class="text-xl font-semibold text-base-content">
                  Your report is ready
                </h3>
                <p class="mb-6 mt-2 max-w-xs text-sm leading-relaxed text-base-content/55">
                  The file was generated successfully and is ready to download.
                </p>
                <a
                  href={downloadUrl.value}
                  download
                  class="btn btn-primary w-full max-w-xs gap-2 rounded-xl shadow-lg shadow-primary/20"
                >
                  <span class="material-symbols-outlined">download</span>
                  Download Report
                </a>

                <button
                  class="btn btn-ghost btn-sm mt-3 text-base-content/55"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadUrl.value = null;
                    isLoading.value = false;
                  }}
                >
                  Create another export
                </button>
              </div>
            ) : (
              <div class="rounded-2xl border border-neutral/15 bg-base-100 p-4 shadow-sm">
                {isLoading.value && (
                  <div class="mb-4 flex items-center gap-3 rounded-xl bg-primary/10 p-3 text-primary">
                    <span class="loading loading-spinner loading-sm" />
                    <div>
                      <p class="text-sm font-medium">Preparing your report</p>
                      <p class="text-xs opacity-70">
                        This can take a moment for larger date ranges.
                      </p>
                    </div>
                  </div>
                )}
                <button
                  class="btn btn-primary w-full gap-2 rounded-xl shadow-lg shadow-primary/20"
                  onClick={startReportGeneration}
                  disabled={isLoading.value}
                >
                  {isLoading.value ? (
                    <>Generating Report...</>
                  ) : (
                    <>
                      <span class="material-symbols-outlined">
                        download_for_offline
                      </span>
                      Generate Report
                    </>
                  )}
                </button>
                <p class="mt-3 flex items-center justify-center gap-1.5 text-center text-[11px] text-base-content/40">
                  <span class="material-symbols-outlined text-sm">lock</span>
                  Your export is generated securely
                </p>
              </div>
            )}
          </div>
        </div>
      </RightSheet>
    );
  },
});
